import { getCachedExchangedToken, refreshTokenIfNeeded } from "../utils/api/token-exchange";
import { REST_API_BASE_URL } from "../config/constants";
import { parseRestApiError } from "../utils/restErrorUtils";

// Get exchanged REST API token — validates user session before returning cached token
const getAuthToken = async (forceRefresh = false): Promise<string> => {
  if (forceRefresh) {
    return refreshTokenIfNeeded();
  }
  return getCachedExchangedToken();
};

// Generic REST API client for time series and other REST endpoints
/* 
 Base url: https://restapi.brewer.cloud
 end point for time series: /v1/timeseries
 endPoint for refresh token: v1/token-exchange

*/
export const restApiClient = {
  // Generic GET request
  get: async (baseUrl: string, endpoint: string, params?: Record<string, any>): Promise<any> => {
    let token = await getAuthToken();

    const url = new URL(endpoint, baseUrl);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          // Handle arrays by JSON stringifying them
          const paramValue = Array.isArray(value) ? JSON.stringify(value) : String(value);
          url.searchParams.append(key, paramValue);
        }
      });
    }

    let response = await fetch(url.toString(), {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    // Retry once on 401 with fresh token
    if (response.status === 401) {
      token = await getAuthToken(true);
      response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
    }

    if (!response.ok) {
      let errorMessage = `REST API request failed: ${response.status} ${response.statusText}`;

      try {
        // Try to parse error response body
        const errorBody = await response.text();
        if (errorBody) {
          try {
            // Try to parse as JSON first
            const errorJson = JSON.parse(errorBody);
            errorMessage = parseRestApiError(errorJson);
          } catch {
            // If not JSON, use the raw text response
            errorMessage = parseRestApiError(errorBody);
          }
        }
      } catch {
        // If reading response body fails, use default message
      }

      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  },

  // Generic POST request
  post: async (baseUrl: string, endpoint: string, body?: any): Promise<any> => {
    let token = await getAuthToken();

    const url = new URL(endpoint, baseUrl);

    let response = await fetch(url.toString(), {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    // Retry once on 401 with fresh token
    if (response.status === 401) {
      token = await getAuthToken(true);
      response = await fetch(url.toString(), {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: body ? JSON.stringify(body) : undefined,
      });
    }

    if (!response.ok) {
      let errorMessage = `REST API request failed: ${response.status} ${response.statusText}`;

      try {
        // Try to parse error response body
        const errorBody = await response.text();
        if (errorBody) {
          try {
            // Try to parse as JSON first
            const errorJson = JSON.parse(errorBody);
            errorMessage = parseRestApiError(errorJson);
          } catch {
            // If not JSON, use the raw text response
            errorMessage = parseRestApiError(errorBody);
          }
        }
      } catch {
        // If reading response body fails, use default message
      }

      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  },

  // Generic request for any HTTP method (PUT/PATCH/DELETE and beyond), driven by a
  // spec-selected operation. GET/POST keep their dedicated methods above for
  // backward compatibility; this is used by the generic REST API data source.
  request: async (
    method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
    baseUrl: string,
    endpoint: string,
    params?: Record<string, unknown>,
    body?: unknown,
  ): Promise<unknown> => {
    if (method === "GET") {
      return restApiClient.get(baseUrl, endpoint, params);
    }
    if (method === "POST") {
      return restApiClient.post(baseUrl, endpoint, body);
    }

    let token = await getAuthToken();

    const url = new URL(endpoint, baseUrl);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          const paramValue = Array.isArray(value) ? JSON.stringify(value) : String(value);
          url.searchParams.append(key, paramValue);
        }
      });
    }

    const buildHeaders = (authToken: string) => ({
      Authorization: `Bearer ${authToken}`,
      "Content-Type": "application/json",
    });

    let response = await fetch(url.toString(), {
      method,
      headers: buildHeaders(token),
      body: body ? JSON.stringify(body) : undefined,
    });

    if (response.status === 401) {
      token = await getAuthToken(true);
      response = await fetch(url.toString(), {
        method,
        headers: buildHeaders(token),
        body: body ? JSON.stringify(body) : undefined,
      });
    }

    if (!response.ok) {
      let errorMessage = `REST API request failed: ${response.status} ${response.statusText}`;

      try {
        const errorBody = await response.text();
        if (errorBody) {
          try {
            const errorJson = JSON.parse(errorBody);
            errorMessage = parseRestApiError(errorJson);
          } catch {
            errorMessage = parseRestApiError(errorBody);
          }
        }
      } catch {
        // If reading response body fails, use default message
      }

      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  },
};

export const getWhoAmI = async (): Promise<any> => {
  return restApiClient.get(REST_API_BASE_URL, "/v1/whoami");
};
